import React, { Fragment, useState } from "react";

import QueryComponent from "../QueryComponent/QueryComponent";
import SkeletonGraph from "../Skeletons/SkeletonGraph";
import BarChart from "../Charts/BarChart/BarChart";
import Card from "../common/Card/Card";
import Typography from "../common/Typography/Typography";
import Table from "../common/Table/Table";
import ChartData from "../Charts/types/ChartData";
import SkeletonTable from "../Skeletons/SkeletonTable";
import { ActivityDetail } from "../../types/API/activity/ActivityDetail";
import ViewDropdown from "../Dropdown/FilterDropdown/ViewDropdown/ViewDropdown";
import CardActions from "../common/Card/CardActions";
import ExportButton from "../Export/ExportButton";
import useExportToCsv from "../../hooks/export/useExportCsv";
import formatTimestamp from "../../helpers/formatTimestamp";

interface Props<T, K> {
    serviceGraphCacheKey: string;
    serviceDetailCacheKey: string;
    serviceGraphApiCall: () => Promise<T>;
    serviceDetailApiCall: () => Promise<K>;
}

const PestActivityYTD = <T extends { chartData: ChartData }, K extends { detail: ActivityDetail[] }>({
    serviceGraphCacheKey,
    serviceDetailCacheKey,
    serviceGraphApiCall,
    serviceDetailApiCall,
}: Props<T, K>) => {
    const [view, setView] = useState<"list" | "graph">("graph");
    const title = "Pest activity YTD";

    return (
        <Card padding="large" className="space-y-2  whitespace-nowrap">
            <div className="flex flex-col sm:flex-row gap-1 flex-start sm:items-center">
                <CardActions className="max-w-[20rem] flex-grow">
                    <Typography as="h3" variant="heading">
                        {title}
                    </Typography>
                </CardActions>
                <CardActions className="flex gap-1 justify-between w-full">
                    <div className="flex gap-1">
                        <ViewDropdown
                            value={view}
                            onChange={(value) => setView(value as "list" | "graph")}
                            options={[
                                {
                                    label: "Graph",
                                    value: "graph",
                                    icon: "grid",
                                },
                                {
                                    label: "List",
                                    value: "list",
                                    icon: "list",
                                },
                            ]}
                        />
                    </div>
                    <div className="flex w-full justify-end">
                        {view === "list" && (
                            <QueryComponent
                                cacheKey={serviceDetailCacheKey}
                                service={() => serviceDetailApiCall()}
                                loadingFallback={<Fragment />}
                            >
                                {({ data }) => {
                                    if (!data) return null;

                                    // hooks shouldnt be called in render but we need this for a fast fix.
                                    // eslint-disable-next-line
                                    const { exportCsv } = useExportToCsv({ data: data.detail, name: "Site Visit Actions" });
                                    return <ExportButton exportCsv={exportCsv} />;
                                }}
                            </QueryComponent>
                        )}
                    </div>
                </CardActions>
            </div>

            {view === "graph" && (
                <QueryComponent cacheKey={serviceGraphCacheKey} service={() => serviceGraphApiCall()} loadingFallback={<SkeletonGraph />}>
                    {({ data }) => (data ? <BarChart padding="none" title="" data={data.chartData} /> : null)}
                </QueryComponent>
            )}

            {view === "list" && (
                <QueryComponent cacheKey={serviceDetailCacheKey} service={() => serviceDetailApiCall()} loadingFallback={<SkeletonTable />}>
                    {({ data }) => {
                        if (!data) return null;

                        return (
                            <Table
                                pagination
                                perPage={10}
                                headings={[
                                    {
                                        key: "activity",
                                        label: "Activity",
                                    },
                                    {
                                        key: "ActivityVisitId",
                                        label: "Visit Id",
                                        hiddenMobile: true,
                                    },
                                    {
                                        key: "ActivityAddedVisitServiceType",
                                        label: "Visit Service Type",
                                    },
                                    {
                                        key: "ActivityAddedVisitType",
                                        label: "Visit Type",
                                    },
                                    {
                                        key: "ActivityMonth",
                                        label: "Month",
                                    },
                                    {
                                        key: "ActivitySiteArea",
                                        label: "Site Area",
                                    },
                                    {
                                        key: "Pest",
                                        label: "Pest",
                                    },
                                    {
                                        key: "SiteName",
                                        label: "Site Name",
                                    },
                                    {
                                        key: "SiteNumber",
                                        label: "Site Number",
                                    },
                                ]}
                                data={data.detail}
                            >
                                {(row) => (
                                    <Table.Row key={JSON.stringify(row)}>
                                        <Table.Cell>
                                            <div className="flex flex-col space-y-0.5 md:space-y-0 py-1 md:p-0">
                                                <div className="type-emphasis">{row.ActivityAddedActionID}</div>
                                                <div className="text-pine-600">{formatTimestamp(row.ActivityAddedDate)}</div>
                                            </div>
                                        </Table.Cell>
                                        <Table.Cell hiddenMobile>#{row.ActivityAddedVisit}</Table.Cell>
                                        <Table.Cell>{row.ActivityAddedVisitServiceType}</Table.Cell>
                                        <Table.Cell>{row.ActivityAddedVisitType}</Table.Cell>
                                        <Table.Cell>{row.ActivityMonth}</Table.Cell>
                                        <Table.Cell>{row.ActivitySiteArea}</Table.Cell>
                                        <Table.Cell>{row.Pest}</Table.Cell>
                                        <Table.Cell>{row.SiteName}</Table.Cell>
                                        <Table.Cell>{row.SiteNumber}</Table.Cell>
                                    </Table.Row>
                                )}
                            </Table>
                        );
                    }}
                </QueryComponent>
            )}
        </Card>
    );
};

export default PestActivityYTD;
